import React, { useState } from 'react';
import { ShieldCheck, Zap, MessageCircle, Lock, Check, ChevronDown, ChevronUp, ArrowRight, Clock, Globe } from 'lucide-react';
type Language = 'en' | 'de' | 'es';
const translations = {
  en: {
    hero: {
      headline: 'Stop overpaying for digital services',
      subheadline: 'Private access, fast setup, and personal support — without public pricing.',
      cta: 'Get private access on Telegram',
      note: 'Direct communication. No bots.',
      servicesNote: 'Access to premium digital services at private pricing'
    },
    whatThisIs: {
      title: 'What this is',
      content: 'I help individuals get access to digital services through private sourcing, including setup assistance and support, at prices lower than standard retail. Everything is handled personally and privately via Telegram.'
    },
    howItWorks: {
      title: 'How it works',
      steps: [{
        title: 'Contact via Telegram',
        desc: 'Reach out directly. No automated forms or waiting lists.'
      }, {
        title: 'Request Service',
        desc: 'Tell me what you need. I confirm availability and price.'
      }, {
        title: 'Setup & Support',
        desc: 'I handle the setup and ensure everything works perfectly.'
      }]
    },
    whatYouGet: {
      title: 'What you get',
      subtitle: 'Stop paying retail prices for services that can be sourced more efficiently.',
      items: ['Private pricing (below public retail)', 'Fast delivery (usually same day)', 'Setup assistance included', 'Direct communication (no bots)', '14-day money-back guarantee'],
      guaranteeTitle: 'Ironclad Guarantee',
      guaranteeText: 'Your satisfaction matters more than one transaction. If any issue arises within 14 days and cannot be resolved, you receive a full refund.',
      guaranteeNote: 'No questions asked'
    },
    whyPrivate: {
      title: 'Why private access?',
      content: 'Public pricing is designed for mass users. Private sourcing allows flexibility, faster handling, and better support.',
      benefits: [{
        title: 'Saved Time',
        desc: 'Skip the research and setup process.'
      }, {
        title: 'Reduced Hassle',
        desc: 'I handle the technical details for you.'
      }, {
        title: 'Direct Responsibility',
        desc: 'You deal with a person, not a ticket system.'
      }]
    },
    guarantee: {
      title: '14-Day Money-Back Guarantee',
      content: 'If any issue arises within 14 days of delivery and it cannot be resolved, you receive a full refund — no arguments.'
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [{
        q: 'Is this official or affiliated with the brands?',
        a: 'No. This is a third-party service providing access and setup assistance. I am not affiliated with any brand.'
      }, {
        q: 'How fast is delivery?',
        a: 'Usually same day or within 24 hours, depending on the service requested.'
      }, {
        q: 'What do you need from me?',
        a: 'Only the minimum required to complete setup. Nothing more.'
      }, {
        q: 'Why are prices not listed publicly?',
        a: 'Availability and pricing vary. All quotes are given privately on Telegram to ensure accuracy.'
      }]
    },
    finalCta: {
      title: 'Ready to stop overpaying?',
      button: 'Get private access on Telegram'
    },
    footer: {
      disclaimer: 'This is a third-party digital services assistance platform. Not affiliated with any software or service providers. All services are provided privately with setup support and a 14-day guarantee.',
      copyright: 'Private Digital Concierge. All rights reserved.'
    }
  },
  de: {
    hero: {
      headline: 'Hören Sie auf, für digitale Dienste zu viel zu bezahlen',
      subheadline: 'Privater Zugang, schnelle Einrichtung und persönlicher Support — ohne öffentliche Preise.',
      cta: 'Privaten Zugang auf Telegram erhalten',
      note: 'Direkte Kommunikation. Keine Bots.',
      servicesNote: 'Zugang zu Premium-Digitaldiensten zu privaten Preisen'
    },
    whatThisIs: {
      title: 'Was ist das',
      content: 'Ich helfe Einzelpersonen, Zugang zu digitalen Diensten durch private Beschaffung zu erhalten, einschließlich Einrichtungshilfe und Support, zu Preisen unter dem Standard-Einzelhandel. Alles wird persönlich und privat über Telegram abgewickelt.'
    },
    howItWorks: {
      title: 'So funktioniert es',
      steps: [{
        title: 'Kontakt über Telegram',
        desc: 'Nehmen Sie direkt Kontakt auf. Keine automatisierten Formulare oder Wartelisten.'
      }, {
        title: 'Service anfragen',
        desc: 'Sagen Sie mir, was Sie brauchen. Ich bestätige Verfügbarkeit und Preis.'
      }, {
        title: 'Einrichtung & Support',
        desc: 'Ich kümmere mich um die Einrichtung und stelle sicher, dass alles perfekt funktioniert.'
      }]
    },
    whatYouGet: {
      title: 'Was Sie erhalten',
      subtitle: 'Hören Sie auf, Einzelhandelspreise für Dienste zu zahlen, die effizienter beschafft werden können.',
      items: ['Private Preise (unter öffentlichem Einzelhandel)', 'Schnelle Lieferung (normalerweise am selben Tag)', 'Einrichtungshilfe inklusive', 'Direkte Kommunikation (keine Bots)', '14-Tage-Geld-zurück-Garantie'],
      guaranteeTitle: 'Eiserne Garantie',
      guaranteeText: 'Ihre Zufriedenheit ist wichtiger als eine Transaktion. Wenn innerhalb von 14 Tagen ein Problem auftritt und nicht gelöst werden kann, erhalten Sie eine vollständige Rückerstattung.',
      guaranteeNote: 'Keine Fragen gestellt'
    },
    whyPrivate: {
      title: 'Warum privater Zugang?',
      content: 'Öffentliche Preise sind für Massennutzer konzipiert. Private Beschaffung ermöglicht Flexibilität, schnellere Abwicklung und besseren Support.',
      benefits: [{
        title: 'Zeit gespart',
        desc: 'Überspringen Sie die Recherche und den Einrichtungsprozess.'
      }, {
        title: 'Weniger Aufwand',
        desc: 'Ich kümmere mich um die technischen Details für Sie.'
      }, {
        title: 'Direkte Verantwortung',
        desc: 'Sie haben es mit einer Person zu tun, nicht mit einem Ticketsystem.'
      }]
    },
    guarantee: {
      title: '14-Tage-Geld-zurück-Garantie',
      content: 'Wenn innerhalb von 14 Tagen nach Lieferung ein Problem auftritt und nicht gelöst werden kann, erhalten Sie eine vollständige Rückerstattung — ohne Diskussion.'
    },
    faq: {
      title: 'Häufig gestellte Fragen',
      items: [{
        q: 'Ist dies offiziell oder mit den Marken verbunden?',
        a: 'Nein. Dies ist ein Drittanbieter-Service, der Zugang und Einrichtungshilfe bietet. Ich bin mit keiner Marke verbunden.'
      }, {
        q: 'Wie schnell ist die Lieferung?',
        a: 'Normalerweise am selben Tag oder innerhalb von 24 Stunden, abhängig vom angeforderten Service.'
      }, {
        q: 'Was benötigen Sie von mir?',
        a: 'Nur das Minimum, das für die Einrichtung erforderlich ist. Nicht mehr.'
      }, {
        q: 'Warum sind die Preise nicht öffentlich aufgeführt?',
        a: 'Verfügbarkeit und Preise variieren. Alle Angebote werden privat auf Telegram gegeben, um Genauigkeit zu gewährleisten.'
      }]
    },
    finalCta: {
      title: 'Bereit, nicht mehr zu viel zu bezahlen?',
      button: 'Privaten Zugang auf Telegram erhalten'
    },
    footer: {
      disclaimer: 'Dies ist eine Drittanbieter-Plattform für digitale Dienstleistungen. Keine Verbindung zu Software- oder Dienstleistern. Alle Dienste werden privat mit Einrichtungssupport und 14-Tage-Garantie bereitgestellt.',
      copyright: 'Private Digital Concierge. Alle Rechte vorbehalten.'
    }
  },
  es: {
    hero: {
      headline: 'Deja de pagar de más por servicios digitales',
      subheadline: 'Acceso privado, configuración rápida y soporte personal — sin precios públicos.',
      cta: 'Obtener acceso privado en Telegram',
      note: 'Comunicación directa. Sin bots.',
      servicesNote: 'Acceso a servicios digitales premium a precios privados'
    },
    whatThisIs: {
      title: 'Qué es esto',
      content: 'Ayudo a personas a obtener acceso a servicios digitales mediante adquisición privada, incluyendo asistencia de configuración y soporte, a precios más bajos que el comercio minorista estándar. Todo se maneja personal y privadamente a través de Telegram.'
    },
    howItWorks: {
      title: 'Cómo funciona',
      steps: [{
        title: 'Contactar por Telegram',
        desc: 'Comunícate directamente. Sin formularios automatizados ni listas de espera.'
      }, {
        title: 'Solicitar servicio',
        desc: 'Dime lo que necesitas. Confirmo disponibilidad y precio.'
      }, {
        title: 'Configuración y soporte',
        desc: 'Me encargo de la configuración y me aseguro de que todo funcione perfectamente.'
      }]
    },
    whatYouGet: {
      title: 'Qué obtienes',
      subtitle: 'Deja de pagar precios minoristas por servicios que pueden obtenerse de manera más eficiente.',
      items: ['Precios privados (por debajo del comercio minorista público)', 'Entrega rápida (generalmente el mismo día)', 'Asistencia de configuración incluida', 'Comunicación directa (sin bots)', 'Garantía de devolución de dinero de 14 días'],
      guaranteeTitle: 'Garantía Férrea',
      guaranteeText: 'Tu satisfacción importa más que una transacción. Si surge algún problema dentro de los 14 días y no se puede resolver, recibes un reembolso completo.',
      guaranteeNote: 'Sin preguntas'
    },
    whyPrivate: {
      title: '¿Por qué acceso privado?',
      content: 'Los precios públicos están diseñados para usuarios masivos. La adquisición privada permite flexibilidad, manejo más rápido y mejor soporte.',
      benefits: [{
        title: 'Tiempo ahorrado',
        desc: 'Omite el proceso de investigación y configuración.'
      }, {
        title: 'Menos complicaciones',
        desc: 'Me encargo de los detalles técnicos por ti.'
      }, {
        title: 'Responsabilidad directa',
        desc: 'Tratas con una persona, no con un sistema de tickets.'
      }]
    },
    guarantee: {
      title: 'Garantía de Devolución de Dinero de 14 Días',
      content: 'Si surge algún problema dentro de los 14 días posteriores a la entrega y no se puede resolver, recibes un reembolso completo — sin discusiones.'
    },
    faq: {
      title: 'Preguntas Frecuentes',
      items: [{
        q: '¿Es oficial o está afiliado con las marcas?',
        a: 'No. Este es un servicio de terceros que proporciona acceso y asistencia de configuración. No estoy afiliado con ninguna marca.'
      }, {
        q: '¿Qué tan rápida es la entrega?',
        a: 'Generalmente el mismo día o dentro de las 24 horas, dependiendo del servicio solicitado.'
      }, {
        q: '¿Qué necesitas de mí?',
        a: 'Solo lo mínimo requerido para completar la configuración. Nada más.'
      }, {
        q: '¿Por qué los precios no están listados públicamente?',
        a: 'La disponibilidad y los precios varían. Todas las cotizaciones se dan privadamente en Telegram para garantizar precisión.'
      }]
    },
    finalCta: {
      title: '¿Listo para dejar de pagar de más?',
      button: 'Obtener acceso privado en Telegram'
    },
    footer: {
      disclaimer: 'Esta es una plataforma de asistencia de servicios digitales de terceros. No está afiliada con ningún proveedor de software o servicios. Todos los servicios se proporcionan privadamente con soporte de configuración y garantía de 14 días.',
      copyright: 'Private Digital Concierge. Todos los derechos reservados.'
    }
  }
};
const languageOptions = [{
  code: 'es' as Language,
  name: 'Español',
  flag: '🇪🇸'
}, {
  code: 'en' as Language,
  name: 'English',
  flag: '🇬🇧'
}, {
  code: 'de' as Language,
  name: 'Deutsch',
  flag: '🇩🇪'
}];
export function LandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [language, setLanguage] = useState<Language>('en');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const t = translations[language];
  const currentLanguage = languageOptions.find(lang => lang.code === language);
  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };
  const changeLanguage = (lang: Language) => {
    setLanguage(lang);
    setShowLanguageMenu(false);
  };
  return <div className="min-h-screen bg-white text-gray-900 font-sans selection:bg-gray-200" data-id="element-2">
      {/* 1. Hero Section */}
      <section className="pt-24 pb-16 px-6 md:pt-32 md:pb-24 max-w-4xl mx-auto text-center" data-id="element-3">
        {/* Logo with floating brand icons */}
        <div className="mb-16 relative" data-id="element-4">
          <div className="relative inline-block" data-id="element-5">
            {/* Splat-shaped background for main logo */}
            <div className="absolute inset-0 bg-gradient-to-br from-green-400 via-emerald-500 to-teal-500 opacity-20 blur-sm" style={{
            clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
            transform: 'scale(1.1)'
          }} data-id="element-6"></div>

            {/* Main Logo - Much Bigger */}
            <img src="https://cdn.magicpatterns.com/uploads/nz9fiQTvKLa84b5PZU6PUa/IMG_9833_GOOD_upscayl_2x_digital-art-4x.png" alt="OffGrid Access" className="h-48 md:h-64 mx-auto relative z-10" data-id="element-7" />

            {/* Floating Brand Icons */}
            <div className="absolute inset-0 pointer-events-none" data-id="element-8">
              {/* ChatGPT - Top Left */}
              <div className="absolute -top-4 -left-12 md:-left-20 w-12 h-12 md:w-16 md:h-16 bg-white rounded-xl shadow-lg flex items-center justify-center border border-gray-100" data-id="element-9">
                <svg className="w-6 h-6 md:w-8 md:h-8" viewBox="0 0 24 24" fill="none" data-id="element-10">
                  <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" fill="#10A37F" data-id="element-11" />
                </svg>
              </div>

              {/* Netflix - Top Right */}
              <div className="absolute -top-4 -right-12 md:-right-20 w-12 h-12 md:w-16 md:h-16 bg-black rounded-xl shadow-lg flex items-center justify-center" data-id="element-12">
                <span className="text-red-600 font-bold text-xl md:text-2xl" data-id="element-13">
                  N
                </span>
              </div>

              {/* Google AI - Left */}
              <div className="absolute top-1/2 -translate-y-1/2 -left-16 md:-left-24 w-12 h-12 md:w-16 md:h-16 bg-white rounded-xl shadow-lg flex items-center justify-center border border-gray-100" data-id="element-14">
                <svg className="w-6 h-6 md:w-8 md:h-8" viewBox="0 0 24 24" data-id="element-15">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" data-id="element-16" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" data-id="element-17" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" data-id="element-18" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" data-id="element-19" />
                </svg>
              </div>

              {/* HBO - Right */}
              <div className="absolute top-1/2 -translate-y-1/2 -right-16 md:-right-24 w-12 h-12 md:w-16 md:h-16 bg-purple-700 rounded-xl shadow-lg flex items-center justify-center" data-id="element-20">
                <span className="text-white font-bold text-sm md:text-base" data-id="element-21">
                  HBO
                </span>
              </div>

              {/* Spotify - Bottom Left */}
              <div className="absolute -bottom-4 -left-12 md:-left-20 w-12 h-12 md:w-16 md:h-16 bg-green-500 rounded-xl shadow-lg flex items-center justify-center" data-id="element-22">
                <svg className="w-6 h-6 md:w-8 md:h-8" viewBox="0 0 24 24" fill="white" data-id="element-23">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z" data-id="element-24" />
                </svg>
              </div>

              {/* YouTube Premium - Bottom Right */}
              <div className="absolute -bottom-4 -right-12 md:-right-20 w-12 h-12 md:w-16 md:h-16 bg-red-600 rounded-xl shadow-lg flex items-center justify-center" data-id="element-25">
                <svg className="w-6 h-6 md:w-8 md:h-8" viewBox="0 0 24 24" fill="white" data-id="element-26">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" data-id="element-27" />
                </svg>
              </div>
            </div>
          </div>

          <p className="mt-8 text-sm text-gray-500 font-medium" data-id="element-28">
            {t.hero.servicesNote}
          </p>
        </div>

        {/* Language Dropdown - Dark style like image */}
        <div className="mb-8 relative inline-block" data-id="element-29">
          <button onClick={() => setShowLanguageMenu(!showLanguageMenu)} className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-full shadow-lg hover:bg-gray-800 transition-colors min-w-[140px]" data-id="element-30">
            <span className="text-xl" data-id="element-31">{currentLanguage?.flag}</span>
            <span className="text-xs font-medium flex-1 text-left" data-id="element-32">
              {currentLanguage?.name}
            </span>
            {showLanguageMenu ? <ChevronUp className="w-3 h-3 text-gray-400" data-id="element-33" /> : <ChevronDown className="w-3 h-3 text-gray-400" data-id="element-34" />}
          </button>

          {showLanguageMenu && <div className="absolute top-full mt-2 left-0 w-full bg-gray-900 rounded-2xl shadow-2xl overflow-hidden z-50 border border-gray-800" data-id="element-35">
              {languageOptions.map(lang => <button key={lang.code} onClick={() => changeLanguage(lang.code)} className={`w-full flex items-center gap-2 px-4 py-2 text-white hover:bg-gray-800 transition-colors ${language === lang.code ? 'bg-gray-800' : ''}`} data-id="element-36">
                  <span className="text-xl" data-id="element-37">{lang.flag}</span>
                  <span className="text-xs font-medium" data-id="element-38">{lang.name}</span>
                </button>)}
            </div>}
        </div>

        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-6 leading-tight text-gray-900" data-id="element-39">
          {t.hero.headline.split(' ').slice(0, 3).join(' ')}{' '}
          <br className="hidden md:block" data-id="element-40" />
          {t.hero.headline.split(' ').slice(3).join(' ')}
        </h1>
        <p className="text-xl md:text-2xl text-gray-600 mb-10 leading-relaxed max-w-2xl mx-auto font-normal" data-id="element-41">
          {t.hero.subheadline}
        </p>
        <a href="https://t.me/OffGridAccessAM" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white rounded-lg transition-colors shadow-sm hover:shadow-md w-full md:w-auto relative overflow-hidden" style={{
        backgroundColor: '#0088cc'
      }} data-id="element-42">
          {/* Telegram logo background */}
          <svg className="absolute right-2 top-1/2 -translate-y-1/2 opacity-20 pointer-events-none" width="80" height="80" viewBox="0 0 24 24" fill="white" data-id="element-43">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" data-id="element-44" />
          </svg>
          <MessageCircle className="w-5 h-5 mr-2 relative z-10" data-id="element-45" />
          <span className="relative z-10" data-id="element-46">{t.hero.cta}</span>
        </a>
        <p className="mt-4 text-sm text-gray-500" data-id="element-47">{t.hero.note}</p>
      </section>

      {/* 2. What this is */}
      <section className="py-16 px-6 bg-gray-50 border-y border-gray-100" data-id="element-48">
        <div className="max-w-2xl mx-auto text-center" data-id="element-49">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4" data-id="element-50">
            {t.whatThisIs.title}
          </h2>
          <p className="text-lg md:text-xl text-gray-800 leading-relaxed" data-id="element-51">
            {t.whatThisIs.content}
          </p>
        </div>
      </section>

      {/* 3. How it works */}
      <section className="py-20 px-6 max-w-5xl mx-auto" data-id="element-52">
        <h2 className="text-3xl font-bold text-center mb-16 tracking-tight" data-id="element-53">
          {t.howItWorks.title}
        </h2>
        <div className="grid md:grid-cols-3 gap-12 relative" data-id="element-54">
          {/* Connecting line for desktop */}
          <div className="hidden md:block absolute top-12 left-0 w-full h-0.5 bg-gray-100 -z-10" data-id="element-55"></div>

          {t.howItWorks.steps.map((item, idx) => <div key={idx} className="flex flex-col items-center text-center bg-white" data-id="element-56">
              <div className="w-24 h-24 rounded-full bg-white border-4 border-gray-50 flex items-center justify-center mb-6 shadow-sm" data-id="element-57">
                {idx === 0 && <MessageCircle className="w-6 h-6 text-gray-900" data-id="element-58" />}
                {idx === 1 && <Zap className="w-6 h-6 text-gray-900" data-id="element-59" />}
                {idx === 2 && <Check className="w-6 h-6 text-gray-900" data-id="element-60" />}
              </div>
              <span className="text-sm font-bold text-gray-400 mb-2" data-id="element-61">
                STEP 0{idx + 1}
              </span>
              <h3 className="text-xl font-bold mb-3" data-id="element-62">{item.title}</h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-63">{item.desc}</p>
            </div>)}
        </div>
      </section>

      {/* 4. What you get */}
      <section className="py-20 px-6 bg-gray-900 text-white" data-id="element-64">
        <div className="max-w-4xl mx-auto" data-id="element-65">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-66">
            <div data-id="element-67">
              <h2 className="text-3xl font-bold mb-6" data-id="element-68">{t.whatYouGet.title}</h2>
              <p className="text-gray-400 mb-8 text-lg" data-id="element-69">
                {t.whatYouGet.subtitle}
              </p>
              <ul className="space-y-4" data-id="element-70">
                {t.whatYouGet.items.map((item, idx) => <li key={idx} className="flex items-start" data-id="element-71">
                    <Check className="w-5 h-5 text-green-400 mr-3 mt-1 flex-shrink-0" data-id="element-72" />
                    <span className="text-gray-200 text-lg" data-id="element-73">{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="bg-gray-800 p-8 rounded-2xl border border-gray-700" data-id="element-74">
              <div className="flex items-center mb-6" data-id="element-75">
                <ShieldCheck className="w-8 h-8 text-white mr-3" data-id="element-76" />
                <h3 className="text-xl font-bold" data-id="element-77">
                  {t.whatYouGet.guaranteeTitle}
                </h3>
              </div>
              <p className="text-gray-300 mb-6 leading-relaxed" data-id="element-78">
                {t.whatYouGet.guaranteeText}
              </p>
              <div className="text-sm text-gray-400 font-medium uppercase tracking-wider" data-id="element-79">
                {t.whatYouGet.guaranteeNote}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. Why private access */}
      <section className="py-20 px-6 max-w-3xl mx-auto text-center" data-id="element-80">
        <h2 className="text-3xl font-bold mb-8" data-id="element-81">{t.whyPrivate.title}</h2>
        <p className="text-lg text-gray-600 mb-10 leading-relaxed" data-id="element-82">
          {t.whyPrivate.content}
        </p>
        <div className="grid sm:grid-cols-3 gap-6 text-left" data-id="element-83">
          {t.whyPrivate.benefits.map((item, idx) => <div key={idx} className="p-6 bg-gray-50 rounded-xl border border-gray-100" data-id="element-84">
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-85">{item.title}</h3>
              <p className="text-sm text-gray-600" data-id="element-86">{item.desc}</p>
            </div>)}
        </div>
      </section>

      {/* 6. Guarantee Section */}
      <section className="py-16 px-6 bg-gray-50 border-y border-gray-200" data-id="element-87">
        <div className="max-w-3xl mx-auto text-center" data-id="element-88">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full shadow-sm mb-6 text-gray-900" data-id="element-89">
            <ShieldCheck className="w-8 h-8" data-id="element-90" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-4" data-id="element-91">
            {t.guarantee.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-92">
            {t.guarantee.content}
          </p>
        </div>
      </section>

      {/* 7. FAQ Section */}
      <section className="py-20 px-6 max-w-3xl mx-auto" data-id="element-93">
        <h2 className="text-3xl font-bold text-center mb-12" data-id="element-94">{t.faq.title}</h2>
        <div className="space-y-4" data-id="element-95">
          {t.faq.items.map((faq, idx) => <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-96">
              <button onClick={() => toggleFaq(idx)} className="w-full flex justify-between items-center p-5 text-left bg-white hover:bg-gray-50 transition-colors" data-id="element-97">
                <span className="font-medium text-gray-900" data-id="element-98">{faq.q}</span>
                {openFaq === idx ? <ChevronUp className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" data-id="element-99" /> : <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0 ml-4" data-id="element-100" />}
              </button>
              {openFaq === idx && <div className="p-5 pt-0 text-gray-600 bg-white border-t border-gray-100" data-id="element-101">
                  {faq.a}
                </div>}
            </div>)}
        </div>
      </section>

      {/* 8. Final Call to Action */}
      <section className="py-24 px-6 text-center" data-id="element-102">
        <h2 className="text-3xl md:text-4xl font-bold mb-8 tracking-tight" data-id="element-103">
          {t.finalCta.title}
        </h2>
        <a href="https://t.me/OffGridAccessAM" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white rounded-lg transition-colors shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 relative overflow-hidden" style={{
        backgroundColor: '#0088cc'
      }} data-id="element-104">
          {/* Telegram logo background */}
          <svg className="absolute right-2 top-1/2 -translate-y-1/2 opacity-20 pointer-events-none" width="80" height="80" viewBox="0 0 24 24" fill="white" data-id="element-105">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" data-id="element-106" />
          </svg>
          <MessageCircle className="w-5 h-5 mr-2 relative z-10" data-id="element-107" />
          <span className="relative z-10" data-id="element-108">{t.finalCta.button}</span>
        </a>
      </section>

      {/* 9. Footer */}
      <footer className="py-12 px-6 border-t border-gray-200 bg-gray-50" data-id="element-109">
        <div className="max-w-3xl mx-auto text-center" data-id="element-110">
          <p className="text-xs text-gray-500 leading-relaxed mb-4" data-id="element-111">
            {t.footer.disclaimer}
          </p>
          <p className="text-xs text-gray-400" data-id="element-112">
            &copy; {new Date().getFullYear()} {t.footer.copyright}
          </p>
        </div>
      </footer>
    </div>;
}